import React, { useState, useEffect, Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { getUserSummaryReport } from '../../actions/dashboard';
import { 
    Row, 
    Col, 
    Card, 
    Button, 
    Form, 
    Alert, 
    Spinner, 
    Table, 
    Badge,
    InputGroup
} from 'react-bootstrap';
import { 
    Users, 
    Download, 
    Search, 
    RefreshCw,
    MapPin,
    Calendar,
    Phone,
    Mail,
    Building2,
    ShoppingCart,
    CheckCircle,
    XCircle
} from 'lucide-react';

const UserSummaryReport = ({ 
    getUserSummaryReport, 
    userSummaryReport, 
    reportLoading, 
    reportError 
}) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [filteredData, setFilteredData] = useState([]);
    const [filters, setFilters] = useState({
        registeredFrom: '',
        registeredTo: '',
        firstPurchaseFrom: '',
        firstPurchaseTo: ''
    });

    useEffect(() => {
        getUserSummaryReport();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (userSummaryReport && userSummaryReport.data) {
            let filtered = [...userSummaryReport.data];
            
            if (searchTerm.trim()) {
                const searchLower = searchTerm.toLowerCase();
                filtered = filtered.filter(user => 
                    (user.userName && user.userName.toLowerCase().includes(searchLower)) ||
                    (user.userPhone && user.userPhone.toLowerCase().includes(searchLower)) ||
                    (user.userEmail && user.userEmail.toLowerCase().includes(searchLower)) ||
                    (user.serviceAreaSector && user.serviceAreaSector.toLowerCase().includes(searchLower))
                );
            }
            
            setFilteredData(filtered);
        }
    }, [userSummaryReport, searchTerm]);

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleExportExcel = () => {
        const filterParams = {
            ...(filters.registeredFrom && { registeredFrom: filters.registeredFrom }),
            ...(filters.registeredTo && { registeredTo: filters.registeredTo }),
            ...(filters.firstPurchaseFrom && { firstPurchaseFrom: filters.firstPurchaseFrom }),
            ...(filters.firstPurchaseTo && { firstPurchaseTo: filters.firstPurchaseTo })
        };
        getUserSummaryReport(filterParams, true);
    };

    const handleRefresh = () => {
        const filterParams = {
            ...(filters.registeredFrom && { registeredFrom: filters.registeredFrom }),
            ...(filters.registeredTo && { registeredTo: filters.registeredTo }),
            ...(filters.firstPurchaseFrom && { firstPurchaseFrom: filters.firstPurchaseFrom }),
            ...(filters.firstPurchaseTo && { firstPurchaseTo: filters.firstPurchaseTo })
        };
        getUserSummaryReport(filterParams, false);
    };

    const handleClearFilters = () => {
        setFilters({
            registeredFrom: '',
            registeredTo: '',
            firstPurchaseFrom: '',
            firstPurchaseTo: ''
        });
        getUserSummaryReport({}, false);
    };

    const formatDate = (dateString) => {
        if (!dateString || dateString === 'N/A') return 'N/A';
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const handleViewOnMap = (coordinates) => {
        if (!coordinates || coordinates === 'N/A') return;
        const [lat, lng] = coordinates.split(',').map(c => c.trim());
        const googleMapsUrl = process.env.REACT_APP_GOOGLE_MAPS_URL || 'https://www.google.com/maps';
        const mapsUrl = `${googleMapsUrl}?q=${lat},${lng}`;
        window.open(mapsUrl, '_blank', 'noopener,noreferrer');
    };

    const getYesNoBadge = (value) => {
        if (value === 'Yes') {
            return <Badge bg="success">Yes</Badge>;
        } else if (value === 'No') {
            return <Badge bg="danger">No</Badge>;
        } else {
            return <Badge bg="secondary">N/A</Badge>;
        }
    };

    return (
        <Fragment>
            <div className="container-fluid py-4" style={{background: '#f8fafc', minHeight: '100vh'}}>
                {/* Header */}
                <div className="mb-4">
                    <div className="d-flex justify-content-between align-items-center flex-wrap">
                        <div>
                            <h2 className="text-primary mb-1">
                                <Users size={32} className="me-3" />
                                User Summary Report
                            </h2>
                            <p className="text-muted mb-0">
                                Comprehensive summary of all users with service area, cart, and purchase information
                            </p>
                        </div>
                        <div className="d-flex gap-2">
                            <Button
                                variant="outline-primary"
                                onClick={handleRefresh}
                                disabled={reportLoading}
                            >
                                <RefreshCw size={16} className="me-2" />
                                Refresh
                            </Button>
                            <Button
                                variant="success"
                                onClick={handleExportExcel}
                                disabled={reportLoading}
                            >
                                <Download size={16} className="me-2" />
                                Export Excel
                            </Button>
                        </div>
                    </div>
                </div>

                {/* Error Alert */}
                {reportError && (
                    <Alert variant="danger" dismissible>
                        <i className="fas fa-exclamation-circle me-2"></i>
                        {reportError}
                    </Alert>
                )}

                {/* Filters */}
                <Card className="mb-4 shadow-sm">
                    <Card.Header className="bg-light">
                        <h5 className="mb-0">
                            <i className="fas fa-filter me-2"></i>
                            Filters
                        </h5>
                    </Card.Header>
                    <Card.Body>
                        <Row>
                            <Col md={12}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Search</Form.Label>
                                    <InputGroup>
                                        <InputGroup.Text>
                                            <Search size={16} />
                                        </InputGroup.Text>
                                        <Form.Control
                                            type="text"
                                            placeholder="Search by name, phone, email, or sector"
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                        />
                                    </InputGroup>
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Registered From</Form.Label>
                                    <Form.Control
                                        type="date"
                                        name="registeredFrom"
                                        value={filters.registeredFrom}
                                        onChange={handleFilterChange}
                                    />
                                    <Form.Text className="text-muted">
                                        Filter users registered from this date
                                    </Form.Text>
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Registered To</Form.Label>
                                    <Form.Control
                                        type="date"
                                        name="registeredTo"
                                        value={filters.registeredTo}
                                        onChange={handleFilterChange}
                                    />
                                    <Form.Text className="text-muted">
                                        Filter users registered until this date
                                    </Form.Text>
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>First Purchase From</Form.Label>
                                    <Form.Control
                                        type="date"
                                        name="firstPurchaseFrom"
                                        value={filters.firstPurchaseFrom}
                                        onChange={handleFilterChange}
                                    />
                                    <Form.Text className="text-muted">
                                        Filter users with first purchase from this date
                                    </Form.Text>
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>First Purchase To</Form.Label>
                                    <Form.Control
                                        type="date"
                                        name="firstPurchaseTo"
                                        value={filters.firstPurchaseTo}
                                        onChange={handleFilterChange}
                                    />
                                    <Form.Text className="text-muted">
                                        Filter users with first purchase until this date
                                    </Form.Text>
                                </Form.Group>
                            </Col>
                        </Row>
                        <div className="d-flex gap-2">
                            <Button
                                variant="primary"
                                onClick={handleRefresh}
                                disabled={reportLoading}
                            >
                                {reportLoading ? (
                                    <>
                                        <Spinner size="sm" className="me-2" />
                                        Loading...
                                    </>
                                ) : (
                                    <>
                                        <Search size={16} className="me-2" />
                                        Apply Filters
                                    </>
                                )}
                            </Button>
                            <Button
                                variant="outline-secondary"
                                onClick={handleClearFilters}
                                disabled={reportLoading}
                            >
                                Clear Filters
                            </Button>
                        </div>
                    </Card.Body>
                </Card>

                {/* Loading State */}
                {reportLoading && (
                    <div className="d-flex justify-content-center align-items-center" style={{minHeight: '400px'}}>
                        <div className="text-center">
                            <Spinner animation="border" variant="primary" />
                            <p className="mt-3 text-muted">Loading user summary report...</p>
                        </div>
                    </div>
                )}

                {/* Report Data */}
                {!reportLoading && userSummaryReport && (
                    <Card className="shadow-sm">
                        <Card.Header className="bg-primary text-white d-flex justify-content-between align-items-center">
                            <h5 className="mb-0">
                                <i className="fas fa-table me-2"></i>
                                User Summary Data
                            </h5>
                            <Badge bg="light" text="dark" className="fs-6">
                                {userSummaryReport.count || filteredData.length} users
                            </Badge>
                        </Card.Header>
                        <Card.Body className="p-0">
                            {filteredData.length > 0 ? (
                                <div className="table-responsive">
                                    <Table striped hover responsive className="mb-0">
                                        <thead className="table-light">
                                            <tr>
                                                <th>Phone</th>
                                                <th>Name</th>
                                                <th style={{ width: '150px', maxWidth: '150px' }}>Email</th>
                                                <th>Registration Date</th>
                                                <th>Service Area Coordinates</th>
                                                <th>Service Area Sector</th>
                                                <th>Service Area</th>
                                                <th>Cart Created</th>
                                                <th>Purchased</th>
                                                <th>Delivery Cart Created</th>
                                                <th>Delivery Request Made</th>
                                                <th>First Purchase Date</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filteredData.map((user, index) => (
                                                <tr key={index}>
                                                    <td>
                                                        <div className="d-flex align-items-center">
                                                            <Phone size={14} className="me-1 text-muted" />
                                                            {user.userPhone || 'N/A'}
                                                        </div>
                                                    </td>
                                                    <td className="fw-bold">{user.userName || 'N/A'}</td>
                                                    <td style={{ width: '150px', maxWidth: '150px' }}>
                                                        {user.userEmail && user.userEmail !== 'N/A' ? (
                                                            <div className="d-flex align-items-center">
                                                                <Mail size={14} className="me-1 text-muted flex-shrink-0" />
                                                                <span style={{ maxWidth: '120px' }} title={user.userEmail}>
                                                                    {user.userEmail}
                                                                </span>
                                                            </div>
                                                        ) : (
                                                            'N/A'
                                                        )}
                                                    </td>
                                                    <td>
                                                        <div className="d-flex align-items-center">
                                                            <Calendar size={14} className="me-1 text-muted" />
                                                            {formatDate(user.userRegistrationDate)}
                                                        </div>
                                                    </td>
                                                    <td>
                                                        {user.serviceAreaCoordinates && user.serviceAreaCoordinates !== 'N/A' ? (
                                                            <Button
                                                                variant="outline-primary"
                                                                size="sm"
                                                                onClick={() => handleViewOnMap(user.serviceAreaCoordinates)}
                                                            >
                                                                <MapPin size={12} className="me-1" />
                                                                View Map
                                                            </Button>
                                                        ) : (
                                                            'N/A'
                                                        )}
                                                    </td>
                                                    <td>
                                                        {user.serviceAreaSector ? (
                                                            <div className="d-flex align-items-center">
                                                                <Building2 size={14} className="me-1 text-muted" />
                                                                {user.serviceAreaSector}
                                                            </div>
                                                        ) : (
                                                            'N/A'
                                                        )}
                                                    </td>
                                                    <td>{getYesNoBadge(user.serviceAreaYesNo)}</td>
                                                    <td>{getYesNoBadge(user.cartCreatedYesNo)}</td>
                                                    <td>{getYesNoBadge(user.purchasedYesNo)}</td>
                                                    <td>{getYesNoBadge(user.deliveryCartCreatedYesNo)}</td>
                                                    <td>{getYesNoBadge(user.deliveryRequestMadeYesNo)}</td>
                                                    <td>
                                                        <div className="d-flex align-items-center">
                                                            <Calendar size={14} className="me-1 text-muted" />
                                                            {formatDate(user.firstPurchaseDate)}
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </div>
                            ) : (
                                <div className="text-center py-5">
                                    <Users size={48} className="text-muted mb-3" />
                                    <p className="text-muted">No user summary data found.</p>
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                )}

                {/* No Data State */}
                {!reportLoading && !userSummaryReport && (
                    <Card className="shadow-sm">
                        <Card.Body className="text-center py-5">
                            <Users size={48} className="text-muted mb-3" />
                            <p className="text-muted">No data available. Click refresh to load the report.</p>
                        </Card.Body>
                    </Card>
                )}
            </div>
        </Fragment>
    );
};

UserSummaryReport.propTypes = {
    getUserSummaryReport: PropTypes.func.isRequired,
    userSummaryReport: PropTypes.object,
    reportLoading: PropTypes.bool.isRequired,
    reportError: PropTypes.string
};

const mapStateToProps = state => ({
    userSummaryReport: state.dashboard.userSummaryReport,
    reportLoading: state.dashboard.reportLoading,
    reportError: state.dashboard.reportError
});

export default connect(mapStateToProps, { getUserSummaryReport })(UserSummaryReport);

